const NORMALIZED_OFFSET_REGEX = new RegExp(/^GMT[+-][0-1][0-9]:[0-5][0-9]$/);

export type TimezoneData = {
  name: string;
  offset: string;
};

export type TimezoneObject = {
  name: string;
  timezoneOffset: {
    longOffset: string;
    shortOffset: string;
  };
};

export class TimezoneOffset {
  readonly longOffset: string;
  readonly shortOffset: string;

  constructor(offset: string) {
    if (offset === 'GMT') {
      this.longOffset = 'GMT';
      this.shortOffset = '+00:00';
      return this;
    }

    const normalized = TimezoneOffset.normalizeOffset(offset);

    if (normalized) {
      this.longOffset = normalized;
      this.shortOffset = normalized.replace('GMT', '');
      return this;
    }

    throw new Error('Invalid offset');
  }

  static testLongOffset(offset: string): boolean {
    return NORMALIZED_OFFSET_REGEX.test(offset);
  }

  static sanitizeOffset(offset: string): string {
    return offset.replace(/[^+\-0-9]/g, '');
  }

  static normalizeOffset(offset: string): string | null {
    if (TimezoneOffset.testLongOffset(offset)) return offset;

    const sanitizedOffset = TimezoneOffset.sanitizeOffset(offset);

    const offsetSignal = sanitizedOffset[0];
    const offsetHours = sanitizedOffset.substring(1, 3);
    const offsetMinutes = sanitizedOffset.substring(3) ?? '00';

    const normalizedOffset = `GMT${offsetSignal}${offsetHours}:${offsetMinutes}`;

    return normalizedOffset;
  }

  equals(timezoneOffset: TimezoneOffset) {
    return this.longOffset === timezoneOffset.longOffset;
  }
}

export class Timezone {
  timezoneOffset: TimezoneOffset;

  constructor(
    readonly name: string,
    offset: string
  ) {
    try {
      this.timezoneOffset = new TimezoneOffset(offset);
    } catch (error) {
      throw new Error(`${String(error)} for timezone ${name}: ${offset}`);
    }
  }

  static serialize(value: Timezone): TimezoneData {
    return {
      name: value.name,
      offset: value.offset,
    };
  }

  static deserialize(value: TimezoneData): Timezone {
    return new Timezone(value.name, value.offset);
  }

  static fromObject(timezone: TimezoneObject) {
    return new Timezone(timezone.name, timezone.timezoneOffset.longOffset);
  }

  get offset(): string {
    return this.timezoneOffset.longOffset;
  }

  get shortOffset() {
    return this.timezoneOffset.shortOffset;
  }

  /** Defines if 2 timezone instances represents the same timezone */
  equals(timezone: Timezone) {
    return this.name === timezone.name && this.offset === timezone.offset;
  }

  /**
   * Defines if 2 timezone instances
   * have the same difference in time (+3 hours for example),
   * even if they are different timezone (have different names)
   */
  equivalent(timezone: Timezone) {
    return this.timezoneOffset.equals(new TimezoneOffset(timezone.offset));
  }
}
